import { FC } from 'react'
import {
  Tree,
  getBackendOptions,
  MultiBackend,
  NodeModel,
  DropOptions,
  getDescendants
} from '@minoru/react-dnd-treeview'
import { DndProvider } from 'react-dnd'
import { NodeDataT } from 'shared/types'
import { Button, IconButton, VStack } from '@chakra-ui/react'
import { generateId } from 'controllers/db'
import useTreeOpenHandler from 'components/fileTree/useTreeOpenHandler'
import FileTreePlaceholder from 'components/fileTree/FileTreePlaceholder'
import FileTreeNode from 'components/fileTree/FileTreeNode'
import styles from 'components/fileTree/styles.module.css'
import map from 'lodash/map'
import get from 'lodash/get'
import filter from 'lodash/filter'
import { ReactComponent as IconFolderPlus } from 'shared/assets/bsFolderPlus.svg'

type Props = {
  treeData: NodeModel<NodeDataT>[]
  onChange: (treeData: NodeModel<NodeDataT>[]) => void
  onNodeClick: (id: string) => void
}

const reorderArray = (
  array: NodeModel<NodeDataT>[],
  sourceIndex: number,
  targetIndex: number
) => {
  const newArray = [...array]
  const element = newArray.splice(sourceIndex, 1)[0]
  newArray.splice(targetIndex, 0, element)
  return newArray
}

const FileThree: FC<Props> = ({ treeData, onChange, onNodeClick }) => {
  const { ref, getPipeHeight, toggle, open } = useTreeOpenHandler()

  const onAddFolder = () => {
    const n: NodeModel<NodeDataT> = {
      id: generateId(),
      parent: 'root',
      text: 'New folder',
      droppable: true
    }
    onChange([...treeData, n])
  }

  const handleDrop = (newTree: NodeModel<NodeDataT>[], e: DropOptions) => {
    console.log('handleDrop initiated', { newTree, event: e })

    // onChange(newTree)
    const { dragSourceId, dropTargetId, destinationIndex, relativeIndex } = e

    if (
      typeof dragSourceId === 'undefined' ||
      typeof dropTargetId === 'undefined'
    ) {
      console.log('Drop aborted: Undefined dragSourceId or dropTargetId')
      return
    }
    const start = treeData.find(v => v.id === dragSourceId)
    const end = treeData.find(v => v.id === dropTargetId)

    if (
      start?.parent === dropTargetId &&
      start &&
      typeof destinationIndex === 'number'
    ) {
      console.log('Reordering within the same parent', {
        start,
        destinationIndex,
        relativeIndex
      })
      const output = reorderArray(
        treeData,
        treeData.indexOf(start),
        dropTargetId === 'root' ? destinationIndex - 1 : destinationIndex
      )
      onChange(output)
    }

    if (
      start?.parent !== dropTargetId &&
      start &&
      typeof destinationIndex === 'number'
    ) {
      if (
        getDescendants(treeData, dragSourceId).find(
          el => el.id === dropTargetId
        ) ||
        dropTargetId === dragSourceId ||
        (end && !end?.droppable)
      ) {
        console.log(
          'Invalid drop: Circular reference or non-droppable target',
          { start, end }
        )
        return
      }
      console.log('Moving to a new parent', {
        start,
        newParent: dropTargetId,
        destinationIndex
      })
      const output = reorderArray(
        treeData,
        treeData.indexOf(start),
        destinationIndex
      )
      const newOutput = map(output, el => {
        if (el.id === dragSourceId) {
          return { ...el, parent: dropTargetId }
        } else {
          return el
        }
      })
      onChange(newOutput)
    }
  }

  const onDelete = (id: string | number) => {
    const newTree = filter(treeData, el => el.id !== id)
    onChange(newTree)
  }

  const createSubFolder = (id: string | number) => {
    const n: NodeModel<NodeDataT> = {
      id: generateId(),
      parent: id,
      text: 'New folder',
      droppable: true
    }
    onChange([...treeData, n])
    open(id)
  }

  const onChangeText = (id: string | number, text: string) => {
    const newTree = map(treeData, el => {
      if (el.id === id) {
        return { ...el, text }
      } else {
        return el
      }
    })
    onChange(newTree)
  }

  return (
    <VStack w={'full'} align='start' pl={4}>
      <IconButton
        variant={'ghost'}
        aria-label='add sub folder'
        colorScheme='wood'
        size='sm'
        icon={<IconFolderPlus width={20} />}
        onClick={onAddFolder}
      />
      {/* <Button variant={'outline'} size='sm' onClick={onAddFolder}>
        + add folder
      </Button> */}

      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        <div className={styles.wrapper}>
          <Tree
            ref={ref}
            tree={treeData}
            rootId={'root'}
            onDrop={handleDrop}
            insertDroppableFirst
            enableAnimateExpand
            canDrop={() => true}
            sort={false}
            dropTargetOffset={5}
            placeholderRender={(node, { depth }) => (
              <FileTreePlaceholder node={node} depth={depth} />
            )}
            classes={{
              root: styles.treeRoot,
              placeholder: styles.placeholder,
              dropTarget: styles.dropTarget,
              listItem: styles.listItem
            }}
            render={(node, { depth, isOpen, isDropTarget }) => (
              <FileTreeNode
                getPipeHeight={getPipeHeight}
                node={node}
                depth={depth}
                isOpen={isOpen}
                onClick={() => {
                  if (node.droppable) {
                    toggle(node?.id)
                  } else {
                    const id = get(node, 'data.id')
                    if (id) {
                      onNodeClick(id)
                    }
                  }
                }}
                isDropTarget={isDropTarget}
                treeData={treeData}
                onDelete={onDelete}
                createSubFolder={createSubFolder}
                onChangeText={onChangeText}
              />
            )}
          />
        </div>
      </DndProvider>
    </VStack>
  )
}

export default FileThree
